import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { translate, useT } from '../lib/i18n';
import type { AudienceFilter } from '../lib/types';
import { Button, Field, Input, Select, cn } from './ui';

/**
 * The one filter form.
 *
 * It appears on four screens — the user list, a broadcast's audience, a
 * notification case's conditions and a reward scheme's eligibility — because
 * all four ask the same question, and the backend answers all four from the
 * same predicate set. An operator learns these controls once.
 *
 * The layout follows how they are actually used: the four everyone reaches for
 * are always visible, and the twenty that answer a specific question live
 * behind a disclosure. A form with twenty-four controls on screen is a form
 * nobody uses.
 */
export function AudienceFilters({
  value,
  onChange,
  /** Hides the name and device-id searches, which only look up people. */
  showSearch = true,
}: {
  value: AudienceFilter;
  onChange: (next: AudienceFilter) => void;
  showSearch?: boolean;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const activeCount = countActive(value);

  const set = <K extends keyof AudienceFilter>(
    key: K,
    next: AudienceFilter[K],
  ) => {
    // An empty control contributes nothing rather than an empty string the
    // API would reject.
    const merged = { ...value, [key]: next };
    if (next === undefined || next === '' || next === null) delete merged[key];
    onChange(merged);
  };

  const number = (key: keyof AudienceFilter) => ({
    type: 'number' as const,
    value: (value[key] as number | undefined) ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      set(key, event.target.value === '' ? undefined : Number(event.target.value)),
  });

  const csv = (key: 'countries' | 'languages') => ({
    value: (value[key] ?? []).join(', '),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const codes = event.target.value
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
      set(key, codes.length > 0 ? codes : undefined);
    },
  });

  return (
    <div className="rounded-xl border border-line bg-surface">
      <div
        className={cn(
          'grid gap-3 p-3.5 sm:grid-cols-2 sm:p-4',
          showSearch ? 'lg:grid-cols-3' : 'lg:grid-cols-4',
        )}
      >
        {showSearch && (
          <Field label={t('filters.name')} hint={t('filters.nameHint')}>
            <Input
              type="search"
              value={value.name ?? ''}
              onChange={(event) => set('name', event.target.value || undefined)}
              placeholder={t('filters.namePlaceholder')}
              autoComplete="off"
            />
          </Field>
        )}

        {showSearch && (
          <Field label={t('filters.search')}>
            <Input
              value={value.search ?? ''}
              onChange={(event) => set('search', event.target.value || undefined)}
              placeholder={t('common.searchPlaceholder')}
            />
          </Field>
        )}

        <Field label={t('filters.platform')}>
          <Select
            value={value.platform ?? ''}
            onChange={(event) => set('platform', event.target.value || undefined)}
          >
            <option value="">{t('common.any')}</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="web">Web</option>
          </Select>
        </Field>

        <TriState
          label={t('filters.subscribed')}
          value={value.isSubscribed}
          onChange={(next) => set('isSubscribed', next)}
        />

        <TriState
          label={t('filters.freeTrial')}
          value={value.isFreeTrial}
          onChange={(next) => set('isFreeTrial', next)}
        />

        <TriState
          label={t('filters.pushReady')}
          value={value.hasFcmToken}
          onChange={(next) => set('hasFcmToken', next)}
          trueLabel={t('filters.hasToken')}
          falseLabel={t('filters.noToken')}
        />

        <TriState
          label={t('filters.activeAlarms')}
          value={value.hasActiveAlarms}
          onChange={(next) => set('hasActiveAlarms', next)}
          trueLabel={t('filters.hasActiveAlarms')}
          falseLabel={t('filters.noActiveAlarms')}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5 sm:px-4">
        <Button
          variant="ghost"
          className="text-xs"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          icon={expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        >
          {expanded ? t('filters.fewer') : t('filters.more')}
          {!expanded && activeCount > 0 && (
            <span className="tnum ml-1 rounded-full bg-brand px-1.5 text-[11px] font-semibold text-on-brand">
              {activeCount}
            </span>
          )}
        </Button>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            className="text-xs"
            onClick={() => onChange({})}
            icon={<RotateCcw size={14} />}
          >
            {t('filters.clearAll')}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-line p-3.5 sm:p-4">
          <Group title={t('filters.group.where')}>
            <Field
              label={t('filters.countries')}
              hint={t('filters.countriesHint')}
            >
              <Input placeholder="TR, DE, US" {...csv('countries')} />
            </Field>
            <Field
              label={t('filters.languages')}
              hint={t('filters.languagesHint')}
            >
              <Input placeholder="en, tr" {...csv('languages')} />
            </Field>
            <TriState
              label={t('filters.accountActive')}
              value={value.isActive}
              onChange={(next) => set('isActive', next)}
            />
            <Field label={t('filters.attestation')}>
              <Select
                value={value.integrityStatus ?? ''}
                onChange={(event) =>
                  set('integrityStatus', event.target.value || undefined)
                }
              >
                <option value="">{t('common.any')}</option>
                <option value="verified">{t('filters.attestation.verified')}</option>
                <option value="unverified">
                  {t('filters.attestation.unverified')}
                </option>
                <option value="failed">{t('filters.attestation.failed')}</option>
                <option value="unknown">{t('filters.attestation.unknown')}</option>
              </Select>
            </Field>
            <Field
              label={t('filters.appVersion')}
              hint={t('filters.appVersionHint')}
            >
              <Input
                value={value.appVersion ?? ''}
                onChange={(event) =>
                  set('appVersion', event.target.value.trim() || undefined)
                }
                placeholder="1.4.2"
                inputMode="decimal"
                autoComplete="off"
              />
            </Field>
          </Group>

          <Group title={t('filters.group.hold')}>
            <Field label={t('filters.balanceMin')}><Input {...number('balanceMin')} /></Field>
            <Field label={t('filters.balanceMax')}><Input {...number('balanceMax')} /></Field>
            <Field
              label={t('filters.spendableMin')}
              hint={t('filters.spendableMinHint')}
            >
              <Input {...number('availableBalanceMin')} />
            </Field>
            <Field label={t('filters.spendableMax')}>
              <Input {...number('availableBalanceMax')} />
            </Field>
            <Field label={t('filters.earnedMin')}>
              <Input {...number('earnedBalanceMin')} />
            </Field>
            <Field label={t('filters.earnedMax')}>
              <Input {...number('earnedBalanceMax')} />
            </Field>
          </Group>

          <Group title={t('filters.group.use')}>
            <Field label={t('filters.alarmsMin')}><Input {...number('alarmCountMin')} /></Field>
            <Field label={t('filters.alarmsMax')}><Input {...number('alarmCountMax')} /></Field>
            <Field label={t('filters.enabledAlarmsMin')}>
              <Input {...number('enabledAlarmCountMin')} />
            </Field>
            <Field label={t('filters.enabledAlarmsMax')}>
              <Input {...number('enabledAlarmCountMax')} />
            </Field>
            <Field label={t('filters.wonMin')}>
              <Input {...number('rewardEarnedMin')} />
            </Field>
            <Field label={t('filters.missedMin')}>
              <Input {...number('rewardMissedMin')} />
            </Field>
            <Field
              label={t('filters.wakeRateMin')}
              hint={t('filters.wakeRateMinHint')}
            >
              <Input {...number('wakeRateMin')} />
            </Field>
            <Field label={t('filters.wakeRateMax')}>
              <Input {...number('wakeRateMax')} />
            </Field>
          </Group>

          <Group title={t('filters.group.when')}>
            <Field
              label={t('filters.inactiveMin')}
              hint={t('filters.inactiveMinHint')}
            >
              <Input {...number('inactiveDaysMin')} />
            </Field>
            <Field label={t('filters.inactiveMax')}>
              <Input {...number('inactiveDaysMax')} />
            </Field>
            <Field label={t('filters.registeredAfter')}>
              <Input
                type="date"
                value={dateValue(value.registeredAfter)}
                onChange={(event) =>
                  set('registeredAfter', toIso(event.target.value))
                }
              />
            </Field>
            <Field label={t('filters.registeredBefore')}>
              <Input
                type="date"
                value={dateValue(value.registeredBefore)}
                onChange={(event) =>
                  set('registeredBefore', toIso(event.target.value))
                }
              />
            </Field>
          </Group>
        </div>
      )}
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset>
      <legend className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
        {title}
      </legend>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </fieldset>
  );
}

/**
 * A filter needs three states, not two: "don't filter on this" is a different
 * thing from "false", and collapsing them is how a checkbox quietly excludes
 * half the audience an operator meant to include.
 */
function TriState({
  label,
  value,
  onChange,
  trueLabel,
  falseLabel,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
  trueLabel?: string;
  falseLabel?: string;
}) {
  const t = useT();

  return (
    <Field label={label}>
      <Select
        value={value === undefined ? '' : String(value)}
        onChange={(event) =>
          onChange(
            event.target.value === ''
              ? undefined
              : event.target.value === 'true',
          )
        }
      >
        <option value="">{t('common.any')}</option>
        <option value="true">{trueLabel ?? t('common.yes')}</option>
        <option value="false">{falseLabel ?? t('common.no')}</option>
      </Select>
    </Field>
  );
}

/** A one-line summary of what a stored filter says, for the rule and scheme lists. */
export function AudienceSummary({
  filter,
  className,
}: {
  filter: AudienceFilter;
  className?: string;
}) {
  const parts = describe(filter);

  return (
    <span className={cn('text-xs text-ink-3', className)}>
      {parts.length === 0
        ? translate('filters.summary.everyone')
        : parts.join(' · ')}
    </span>
  );
}

/** Turns a filter into the phrases an operator would say out loud. */
function describe(filter: AudienceFilter): string[] {
  const parts: string[] = [];

  if (filter.platform) parts.push(filter.platform);
  if (filter.countries?.length) parts.push(filter.countries.join('/'));
  if (filter.languages?.length) parts.push(filter.languages.join('/'));
  if (filter.isSubscribed === true) {
    parts.push(translate('filters.summary.subscribed'));
  }
  if (filter.isSubscribed === false) {
    parts.push(translate('filters.summary.notSubscribed'));
  }
  if (filter.isFreeTrial === true) {
    parts.push(translate('filters.summary.onFreeTrial'));
  }
  if (filter.isFreeTrial === false) {
    parts.push(translate('filters.summary.notOnFreeTrial'));
  }
  if (filter.isActive === false) {
    parts.push(translate('filters.summary.deactivated'));
  }
  if (filter.hasFcmToken === false) {
    parts.push(translate('filters.summary.noPushToken'));
  }
  if (filter.hasActiveAlarms === true) {
    parts.push(translate('filters.summary.hasActiveAlarms'));
  }
  if (filter.hasActiveAlarms === false) {
    parts.push(translate('filters.summary.noActiveAlarms'));
  }
  if (filter.inactiveDaysMin !== undefined) {
    parts.push(
      translate('filters.summary.quiet', { days: filter.inactiveDaysMin }),
    );
  }
  if (filter.availableBalanceMax !== undefined) {
    parts.push(
      translate('filters.summary.spendableMax', {
        value: filter.availableBalanceMax,
      }),
    );
  }
  if (filter.availableBalanceMin !== undefined) {
    parts.push(
      translate('filters.summary.spendableMin', {
        value: filter.availableBalanceMin,
      }),
    );
  }
  if (filter.wakeRateMax !== undefined) {
    parts.push(
      translate('filters.summary.wakeRateMax', { value: filter.wakeRateMax }),
    );
  }
  if (filter.wakeRateMin !== undefined) {
    parts.push(
      translate('filters.summary.wakeRateMin', { value: filter.wakeRateMin }),
    );
  }
  if (filter.enabledAlarmCountMax !== undefined) {
    parts.push(
      translate('filters.summary.liveAlarmsMax', {
        value: filter.enabledAlarmCountMax,
      }),
    );
  }
  if (filter.rewardMissedMin !== undefined) {
    parts.push(
      translate('filters.summary.missedMin', { value: filter.rewardMissedMin }),
    );
  }
  if (filter.userIds?.length) {
    parts.push(
      translate('filters.summary.namedUsers', { count: filter.userIds.length }),
    );
  }
  if (filter.deviceIds?.length) {
    parts.push(
      translate('filters.summary.namedDevices', {
        count: filter.deviceIds.length,
      }),
    );
  }
  if (filter.appVersion) {
    parts.push(
      translate('filters.summary.appVersion', { value: filter.appVersion }),
    );
  }
  if (filter.name) {
    parts.push(translate('filters.summary.name', { value: filter.name }));
  }
  if (filter.search) {
    parts.push(translate('filters.summary.idContains', { value: filter.search }));
  }

  return parts;
}

/** How many predicates are set — the badge on the disclosure. */
export function countActive(filter: AudienceFilter): number {
  return Object.values(filter).filter(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0),
  ).length;
}

function dateValue(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

function toIso(date: string): string | undefined {
  if (!date) return undefined;
  return new Date(`${date}T00:00:00`).toISOString();
}
